import { useEffect, useState } from "react";

import { useConfig } from "./components/ConfigProvider";

export type Color = "Green" | "Orange" | "Blue" | "Red" | "Yellow" | "OFF";

const toRGB = (color: Color) => {
    switch (color) {
        case "Green":
            return [1, 255, 1];
        case "Blue":
            return [1, 1, 255];
        case "Orange":
            return [255, 138, 1];
        case "Red":
            return [255, 1, 1];
        case "Yellow":
            return [255, 255, 1];
        case "OFF":
            return [1, 1, 1];
        default:
            return color satisfies never;
    }
};

type LampConfiguration = {
    vendorId: number;
    productId: number;
    options: SerialOptions;
    constructPayload: (color: Color) => Uint8Array;
};

const LAMP_CONFIGURATION: Record<string, LampConfiguration> = {
    // Eventually, this will be the only SCO lamp and we can simplify this file
    PYRAMID: {
        vendorId: 0x03eb,
        productId: 0x2404,
        options: {
            baudRate: 115200,
            dataBits: 8,
            stopBits: 1,
            parity: "none"
        },
        constructPayload: (color) =>
            new Uint8Array([0x5a, 0xff, 0xe8, ...toRGB(color), 0xa5])
    },
    DEPRECATED_PRIMELCO: {
        vendorId: 0x0403,
        productId: 0x6001,
        options: {
            baudRate: 9600
        },
        constructPayload: (color) =>
            new Uint8Array([83, ...toRGB(color), 1, 1, 1, 13, 10])
    }
};

const sleep = (ms: number) =>
    new Promise<void>((resolve) => setTimeout(resolve, ms));

const BLINK_ON_MS = 300;
const BLINK_OFF_MS = 100;

const isSupported = "serial" in navigator;

const getLampConfiguration = ({
    usbVendorId,
    usbProductId
}: Partial<SerialPortInfo>) => {
    if (!usbVendorId || !usbProductId) return undefined;

    return Object.values(LAMP_CONFIGURATION).find(
        ({ vendorId, productId }) =>
            vendorId === usbVendorId && productId === usbProductId
    );
};

const requestPort = async (): Promise<SerialPort> => {
    const port = await navigator.serial.requestPort({
        filters: Object.values(LAMP_CONFIGURATION).map(
            ({ vendorId, productId }) => ({
                usbVendorId: vendorId,
                usbProductId: productId
            })
        )
    });

    return port;
};

const getPort = async (): Promise<SerialPort | undefined> => {
    if (!isSupported) return;

    const ports = await navigator.serial.getPorts();

    const port = ports.find((port) => {
        const info = port.getInfo();
        return getLampConfiguration(info) !== undefined;
    });

    return port;
};

const connect = async (port: SerialPort) => {
    const portInfo = port.getInfo();
    const lampConfiguration = getLampConfiguration(portInfo);

    const write = async (bytes: Uint8Array) => {
        const writer = port.writable?.getWriter();
        if (!writer) return;
        console.log("Writing bytes", bytes);
        await writer.write(bytes);
        writer.releaseLock();
    };

    const writePayload = async (bytes: Uint8Array) => {
        if (!lampConfiguration) return;
        await port.open(lampConfiguration.options).catch(console.warn);
        await write(bytes);
        await port.close().catch(console.warn);
    };

    return {
        setColor: async (color: Color) => {
            if (!lampConfiguration) return;
            const bytes = lampConfiguration.constructPayload(color);
            await writePayload(bytes);
            await consoleLight(color);
        },
        setBlink: async (color: Color, blinks = 2) => {
            if (!lampConfiguration) return;
            const colorBytes = lampConfiguration.constructPayload(color);
            const offBytes = lampConfiguration.constructPayload("OFF");
            await port.open(lampConfiguration.options).catch(console.warn);
            for (let i = 0; i < blinks; i++) {
                await write(colorBytes);
                await sleep(BLINK_ON_MS);
                await write(offBytes);
                await sleep(BLINK_OFF_MS);
            }
            await port.close().catch(console.warn);
        }
    };
};

const consoleLight = async (color: Color) => {
    console.log(`%c ${color}`, `color: rgb(${toRGB(color)})`);
};

const consoleBlink = async (color: Color, blinks = 2) => {
    for (let i = 0; i < blinks; i++) {
        await consoleLight(color);
        await sleep(BLINK_ON_MS);
        console.log("%c Off", "color: rgb(0,0,0)");
        await sleep(BLINK_OFF_MS);
    }
};

export const useLamp = () => {
    const { tenant } = useConfig();

    const connectAndUpdateState = async (port: SerialPort) => {
        const { setColor, setBlink } = await connect(port);
        setState((state) => ({
            ...state,
            connected: true,
            setColor: setColor,
            setBlink: setBlink
        }));
    };

    const [state, setState] = useState({
        connected: false,
        enabled: isSupported,
        connect: () =>
            requestPort()
                .then((port) => connectAndUpdateState(port))
                .catch(() =>
                    setState((state) => ({ ...state, enabled: false }))
                ),
        setColor: consoleLight,
        setBlink: consoleBlink
    });

    useEffect(() => {
        getPort().then((port) => {
            if (port) {
                connectAndUpdateState(port);
            }
        });
    }, [tenant]);

    return state;
};
